// Phase COTTON-RM-002 Part B — Bale QR Print.
//
// Three modes:
//   1. Print missing QR (backfill all bales without QR — dry-run + execute)
//   2. Print by GRN
//   3. Print selected bales
//
// QR rendering uses a placeholder SVG block (text + value). The real
// QR image hook can be added later by swapping the QRPreview component
// for a library like `qrcode.react`. The page already calls
// `mark-printed` on each bale after a print job so the print-count
// audit trail starts working immediately.

import { useMemo, useState } from 'react'
import {
  Alert, App, Button, Card, Checkbox, Col, Empty, Input, Modal, Row,
  Segmented, Select, Skeleton, Space, Table, Tag, Typography,
} from 'antd'
import {
  CheckCircleOutlined, PrinterOutlined, ReloadOutlined, ThunderboltOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import apiClient from '@/shared/api/client'
import { cottonRmApi } from '@/features/cotton-rm/cottonRmApi'

const { Text, Title } = Typography

interface Plant { id: string; plant_name: string }
interface Bale {
  id: string
  bale_no: string
  grn_id: string | null
  lot_no: string | null
  weight_kg: number
  qr_code_value: string | null
  qr_status: string
  qr_print_count: number
  qr_printed_at: string | null
}

type LabelFormat = 'STICKER' | 'TAG' | 'A4_SHEET'

function QRPreview({ value, size = 96 }: { value: string; size?: number }) {
  // Real QR rendering hook — swap for `qrcode.react` when library installed.
  return (
    <div style={{
      width: size, height: size, border: '1px solid #888',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#fafafa', borderRadius: 6, padding: 4, textAlign: 'center',
      wordBreak: 'break-all', fontSize: 9, fontFamily: 'monospace',
    }}>
      {value}
    </div>
  )
}

function LabelCard({ bale, qrValue, format }: {
  bale: Bale; qrValue: string; format: LabelFormat
}) {
  const size = format === 'STICKER' ? 80 : format === 'TAG' ? 140 : 100
  const inner = (
    <Space direction="vertical" align="center" size={4} style={{ width: '100%' }}>
      <QRPreview value={qrValue} size={size} />
      <Text strong style={{ fontSize: format === 'TAG' ? 14 : 11 }}>{bale.bale_no}</Text>
      <Text style={{ fontSize: 10 }}>{bale.lot_no ?? '—'}</Text>
      <Text style={{ fontSize: 10 }}>{bale.weight_kg} kg</Text>
    </Space>
  )
  if (format === 'A4_SHEET') {
    return (
      <Col xs={12} sm={8} md={6} lg={4}>
        <Card size="small" bodyStyle={{ padding: 8 }}>{inner}</Card>
      </Col>
    )
  }
  return <Card size="small" bodyStyle={{ padding: 8 }}>{inner}</Card>
}

export default function BaleQrPrintPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [plantId, setPlantId] = useState<string | undefined>()
  const [mode, setMode] = useState<'BACKFILL' | 'BY_GRN' | 'SELECTED'>('BACKFILL')
  const [grnId, setGrnId] = useState<string | undefined>()
  const [selectedBales, setSelectedBales] = useState<string[]>([])
  const [format, setFormat] = useState<LabelFormat>('STICKER')
  const [previewOpen, setPreviewOpen] = useState(false)

  const { data: plants = [] } = useQuery({
    queryKey: ['plants-for-qr'],
    queryFn: async () => (await apiClient.get<Plant[]>('/plants')).data,
  })

  const { data: grns = [] } = useQuery({
    queryKey: ['grns-for-qr', plantId],
    queryFn: () => apiClient.get('/grn', { params: { plant_id: plantId, limit: 100 } })
      .then(r => r.data),
    enabled: !!plantId,
  })

  const { data: bales = [], isLoading: balesLoading } = useQuery({
    queryKey: ['bales-for-qr', plantId, grnId],
    queryFn: () => apiClient.get<Bale[]>('/bales',
      { params: { plant_id: plantId, limit: 1000 } }).then(r => r.data),
    enabled: !!plantId,
  })

  const filteredBales = useMemo(() => {
    let rows = bales
    if (mode === 'BY_GRN' && grnId) {
      rows = rows.filter(b => b.grn_id === grnId)
    }
    if (mode === 'BACKFILL') {
      rows = rows.filter(b => !b.qr_code_value)
    }
    if (mode === 'SELECTED') {
      rows = rows.filter(b => selectedBales.includes(b.id))
    }
    return rows
  }, [bales, mode, grnId, selectedBales])

  const backfillMut = useMutation({
    mutationFn: (dry: boolean) => cottonRmApi.backfillQr(dry),
    onSuccess: (rep) => {
      message.success(
        `${rep.dry_run ? 'Dry-run' : 'Backfill complete'}: ` +
        `${rep.qr_generated} generated, ${rep.qr_already_present} already had QR, ` +
        `${rep.skipped} skipped.`
      )
      qc.invalidateQueries({ queryKey: ['bales-for-qr', plantId, grnId] })
    },
  })

  const printMut = useMutation({
    mutationFn: async (rows: Bale[]) => {
      // Ensure each bale has a QR (lazy-generate missing ones first)
      for (const b of rows) {
        if (!b.qr_code_value) {
          await cottonRmApi.generateQr(b.id)
        }
        await cottonRmApi.markPrinted(b.id)
      }
      return rows.length
    },
    onSuccess: (n) => {
      message.success(`Marked ${n} bales as printed.`)
      qc.invalidateQueries({ queryKey: ['bales-for-qr', plantId, grnId] })
      setPreviewOpen(false)
    },
  })

  const baleColumns = [
    { title: 'Bale', dataIndex: 'bale_no',
      render: (v: string) => <code>{v}</code> },
    { title: 'Lot', dataIndex: 'lot_no', render: (v: string | null) => v ?? '—' },
    { title: 'Weight', dataIndex: 'weight_kg',
      render: (n: number) => `${n} kg` },
    { title: 'QR', dataIndex: 'qr_code_value',
      render: (v: string | null) => v ?
        <code style={{ fontSize: 11 }}>{v}</code>
        : <Tag color="warning">NOT GENERATED</Tag> },
    { title: 'Status', dataIndex: 'qr_status',
      render: (s: string) => <Tag>{s}</Tag> },
    { title: 'Print count', dataIndex: 'qr_print_count' },
    { title: 'Last printed', dataIndex: 'qr_printed_at',
      render: (s: string | null) => s ? new Date(s).toLocaleString() : '—' },
  ]

  return (
    <div style={{ padding: 16, maxWidth: 1500, margin: '0 auto' }}>
      <Title level={2} style={{ marginTop: 0 }}>Bale QR Print</Title>
      <Text type="secondary">
        Generate + print QR labels for cotton bales. Format:
        <code> UCL:BALE:&lt;plant&gt;:&lt;bale_no&gt;</code>. QR is the bale
        identity layer used across Gate / Warehouse / QC / Mixing / Production.
      </Text>

      <Card style={{ marginTop: 12 }}>
        <Space wrap>
          <Select placeholder="Plant" style={{ width: 220 }}
                  value={plantId} onChange={setPlantId}
                  options={plants.map(p => ({ value: p.id, label: p.plant_name }))} />
          <Segmented value={mode}
                      onChange={(v) => setMode(v as 'BACKFILL' | 'BY_GRN' | 'SELECTED')}
                      options={[
                        { value: 'BACKFILL', label: 'Missing QR (backfill)' },
                        { value: 'BY_GRN', label: 'By GRN' },
                        { value: 'SELECTED', label: 'Selected' },
                      ]} />
          {mode === 'BY_GRN' && (
            <Select placeholder="GRN" style={{ width: 220 }}
                    value={grnId} onChange={setGrnId} allowClear
                    options={grns.map((g: { id: string; grn_no: string }) =>
                      ({ value: g.id, label: g.grn_no }))} />
          )}
          <Segmented value={format}
                      onChange={(v) => setFormat(v as LabelFormat)}
                      options={[
                        { value: 'STICKER', label: 'Sticker' },
                        { value: 'TAG', label: 'Tag' },
                        { value: 'A4_SHEET', label: 'A4 sheet' },
                      ]} />
        </Space>
      </Card>

      {mode === 'BACKFILL' && (
        <Card style={{ marginTop: 12 }} title="Backfill — generate QR for bales that don't have one">
          <Space wrap>
            <Button icon={<ReloadOutlined />}
                     loading={backfillMut.isPending}
                     onClick={() => backfillMut.mutate(true)}>
              Dry-run
            </Button>
            <Button type="primary" icon={<ThunderboltOutlined />}
                     loading={backfillMut.isPending}
                     onClick={() => backfillMut.mutate(false)}>
              Execute Backfill
            </Button>
          </Space>
          <Alert type="info" style={{ marginTop: 8 }} showIcon
                  message="Backfill is idempotent. Existing QR codes are never overwritten." />
        </Card>
      )}

      <Card style={{ marginTop: 12 }}
            title={`Bales (${filteredBales.length})`}
            extra={
              <Space>
                <Button icon={<PrinterOutlined />} type="primary"
                         disabled={filteredBales.length === 0}
                         onClick={() => setPreviewOpen(true)}>
                  Preview + Mark Printed
                </Button>
              </Space>}>
        {!plantId ? <Empty description="Pick a plant." /> :
          balesLoading ? <Skeleton active /> :
          filteredBales.length === 0 ? <Empty description="No bales for these filters." /> : (
            <Table
              rowKey="id"
              rowSelection={mode === 'SELECTED' ? {
                selectedRowKeys: selectedBales,
                onChange: (k) => setSelectedBales(k.map(String)),
              } : undefined}
              columns={baleColumns as never}
              dataSource={filteredBales}
              size="small"
              pagination={{ pageSize: 50 }}
            />
          )}
      </Card>

      <Modal open={previewOpen} title="Print preview" width={900}
              onCancel={() => setPreviewOpen(false)}
              onOk={() => printMut.mutate(filteredBales)}
              okText={<><CheckCircleOutlined /> Mark printed</>}
              confirmLoading={printMut.isPending}>
        <Alert type="info" style={{ marginBottom: 12 }} showIcon
                message={`${filteredBales.length} labels — ${format}.
                Use the browser's Print dialog for paper output, then click
                Mark printed to record the action.`} />
        {format === 'A4_SHEET' ? (
          <Row gutter={[8, 8]}>
            {filteredBales.slice(0, 40).map(b => (
              <LabelCard key={b.id} bale={b}
                          qrValue={b.qr_code_value ?? `(will be generated on print)`}
                          format={format} />
            ))}
          </Row>
        ) : (
          <Space wrap>
            {filteredBales.slice(0, 20).map(b => (
              <LabelCard key={b.id} bale={b}
                          qrValue={b.qr_code_value ?? `(will be generated on print)`}
                          format={format} />
            ))}
          </Space>
        )}
        {filteredBales.length > 40 && (
          <Alert type="warning" style={{ marginTop: 8 }} showIcon
                  message={`Showing first 40 of ${filteredBales.length} for preview.
                  Mark printed will still process all of them.`} />
        )}
      </Modal>
    </div>
  )
}
